
import {AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild, DOCUMENT} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import type {FiltersAndSorting} from '../requests-overview-content/requests-overview-content.component';
import {DeadlineStatusOption, leadSortType, LeadStatus, LeadType} from '../../../../models/leads/leads.model';
import {CONTACTS_DATA_SERVICE, ContactsDataService} from '../../../../services/contacts/contacts-data.service';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../../services/leads/leads-data.service';
import {ContactType} from '../../../../models/contacts/contacts.model';
import {ResourceCountry} from '../../../../models/resources/resources.model';
import {AddressHelper} from '../../../../helper/address.helper';

export type MobileFilterSection =
  'search'
  | 'sort'
  | 'showOnly'
  | 'createdOn'
  | 'type'
  | 'contact'
  | 'country'
  | 'status'
  | 'responsible'
  | 'deadline';

@Component({
  selector: 'fi-requests-overview-mobile-filter-overlay',
  templateUrl: './requests-overview-mobile-filter-overlay.component.html',
  styleUrls: ['./requests-overview-mobile-filter-overlay.component.scss'],
  standalone: false,
})
export class RequestsOverviewMobileFilterOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mobileFilterSearch') mobileFilterSearch: ElementRef<HTMLInputElement>;

  @Input() filters: FiltersAndSorting;
  @Input() isFinstralUser: boolean;
  @Input() categoryTypes: { [key: string]: string }[] = [];
  @Input() initialSection: MobileFilterSection | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() filtersChanged = new EventEmitter<void>();
  @Output() queryChanged = new EventEmitter<string>();
  @Output() categoryTypeChanged = new EventEmitter<string>();
  @Output() rangeSelected = new EventEmitter<Date[]>();
  @Output() sortChanged = new EventEmitter<{ sortType: leadSortType; sortDirection: 'ASC' | 'DESC' }>();

  selectedCategoryKey = '';
  mobileSearchText = '';
  mobileResponsibleSearchText = '';
  countrySearchText = '';
  mobileDateRange: Date[] = [];
  mobileDatePickerOpen = false;
  showAllStatuses = false;
  mobileSortType: leadSortType = 'DATE';
  mobileSortDirection: 'ASC' | 'DESC' = 'ASC';
  expandedMobileSection: MobileFilterSection | null = null;

  readonly mobileSortTypes: { value: leadSortType; labelKey: string }[] = [
    {value: 'TYPE', labelKey: 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_LEAD_TYPE'},
    {value: 'COMMISSION', labelKey: 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_PROJECT'},
    {value: 'CONTACT', labelKey: 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_CONTACT'},
    {value: 'DATE2', labelKey: 'SEARCH_DATE'},
    {value: 'STATUS', labelKey: 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_STATUS'},
    {value: 'RESPONSIBLE', labelKey: 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_RESPONSIBLE'},
    {value: 'DATE', labelKey: 'REQUEST_OVERVIEW_COLUMNS_HEADLINE_DUE_DATE'},
  ];

  leadTypes: LeadType[] = [];
  contactTypes: ContactType[] = [];
  countries: ResourceCountry[] = [];
  leadStatuses: LeadStatus[] = [];
  deadlineStatuses: DeadlineStatusOption[] = [
    {Id: 'green', Text: 'REQUEST_OVERVIEW_FILTER_HIDE_GREEN_STATUS'},
    {Id: 'orange', Text: 'REQUEST_OVERVIEW_FILTER_HIDE_ORANGE_STATUS'},
    {Id: 'red', Text: 'REQUEST_OVERVIEW_FILTER_HIDE_RED_STATUS'},
  ];
  private previousBodyOverflow = '';

  get filteredCountries(): ResourceCountry[] {
    const searchText = this.countrySearchText.trim().toLowerCase();

    if (!searchText) {
      return this.countries;
    }

    return this.countries.filter(country => country.Name?.toLowerCase().includes(searchText));
  }

  get visibleLeadStatuses(): LeadStatus[] {
    return this.showAllStatuses
      ? this.leadStatuses
      : this.leadStatuses.slice(0, 4);
  }

  get shouldShowMoreStatusesButton(): boolean {
    return !this.showAllStatuses && this.leadStatuses.length > 4;
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    @Inject(LEADS_DATA_SERVICE) private leadDataService: LeadsDataService,
  ) {
  }

  ngOnInit(): void {
    this.lockBodyScroll();
    this.selectedCategoryKey = this.getSelectedCategoryKey(this.filters?.categoryKey);
    this.mobileSearchText = this.filters?.search ?? '';
    this.mobileResponsibleSearchText = this.filters?.responsibleSearches?.[0] ?? '';
    this.mobileDateRange = [this.filters?.fromDate, this.filters?.toDate] as Date[];
    this.mobileSortType = this.filters?.sortType ?? 'DATE';
    this.mobileSortDirection = this.filters?.sortDirection ?? 'ASC';
    this.expandedMobileSection = this.initialSection;

    if (this.expandedMobileSection) {
      void this.loadMobileSectionOptions(this.expandedMobileSection);
    }
  }

  ngAfterViewInit(): void {
    if (this.initialSection === 'search') {
      this.focusMobileSearch();
    }
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  async toggleMobileSection(section: MobileFilterSection): Promise<void> {
    this.expandedMobileSection = this.expandedMobileSection === section ? null : section;

    if (this.expandedMobileSection === section) {
      if (section !== 'createdOn') {
        this.mobileDatePickerOpen = false;
      }

      await this.loadMobileSectionOptions(section);

      if (section === 'search') {
        this.focusMobileSearch();
      }
    }
  }

  applyMobileFilters(): void {
    const nextQuery = this.mobileSearchText?.trim() ?? '';
    const hasDateRangeChanged = this.hasDateRangeChanged();
    const hasSortChanged =
      this.mobileSortType !== this.filters.sortType ||
      this.mobileSortDirection !== this.filters.sortDirection;

    if (nextQuery !== (this.filters.search ?? '')) {
      this.queryChanged.emit(nextQuery);
    }

    if (this.selectedCategoryKey !== this.getSelectedCategoryKey(this.filters.categoryKey)) {
      this.categoryTypeChanged.emit(this.selectedCategoryKey);
    }

    this.filters.responsibleSearches = this.mobileResponsibleSearchText?.trim()
      ? [this.mobileResponsibleSearchText.trim()]
      : [];

    if (hasDateRangeChanged) {
      this.rangeSelected.emit([this.mobileDateRange?.[0], this.mobileDateRange?.[1]] as Date[]);
    }

    if (hasSortChanged) {
      this.sortChanged.emit({
        sortType: this.mobileSortType,
        sortDirection: this.mobileSortDirection,
      });
    } else if (!hasDateRangeChanged) {
      this.filtersChanged.emit();
    }

    this.closed.emit();
  }

  clearMobileSearchText(): void {
    this.mobileSearchText = '';
    setTimeout(() => this.mobileFilterSearch?.nativeElement?.focus());
  }

  clearCountrySearchText(): void {
    this.countrySearchText = '';
  }

  clearMobileDateRange(event: MouseEvent): void {
    event.stopPropagation();
    this.mobileDateRange = [];
    this.mobileDatePickerOpen = false;
  }

  onDateRangeSelected(range: Date[]): void {
    this.mobileDateRange = range;

    if (this.mobileDateRange?.[0] && this.mobileDateRange?.[1]) {
      this.mobileDatePickerOpen = false;
    }
  }

  toggleMobileDatePicker(): void {
    this.mobileDatePickerOpen = !this.mobileDatePickerOpen;
  }

  get mobileDateRangeText(): string {
    if (!this.mobileDateRange?.[0] || !this.mobileDateRange?.[1]) {
      return '';
    }

    return [this.mobileDateRange[0], this.mobileDateRange[1]]
      .map(date => this.formatMobileDate(date, false))
      .join(' - ');
  }

  toggleSelection<T>(collectionKey: keyof FiltersAndSorting, option: T, compareKey: keyof T): void {
    const current = ((this.filters[collectionKey] as T[]) ?? []);
    const optionValue = option[compareKey];
    const exists = current.some(item => item?.[compareKey] === optionValue);
    const filters = this.filters as Record<string, unknown>;

    filters[collectionKey] = exists
      ? current.filter(item => item?.[compareKey] !== optionValue)
      : [...current, option];
  }

  isSelected<T>(collection: T[] | undefined, option: T, compareKey: keyof T): boolean {
    return !!collection?.some(item => item?.[compareKey] === option[compareKey]);
  }

  togglePremiumPartner(): void {
    this.filters.premiumProjectCustomerTypes = this.filters.premiumProjectCustomerTypes?.length
      ? []
      : [{Id: 1, Text: 'REQUEST_OVERVIEW_FILTER_PREMIUM_PARTNER'}];
  }

  private async loadMobileSectionOptions(section: MobileFilterSection): Promise<void> {
    switch (section) {
      case 'type':
        if (!this.leadTypes.length) {
          const response = await firstValueFrom(this.leadDataService.getLeadTypes());
          this.leadTypes = response.success ? response.data : [];
        }
        break;
      case 'contact':
        if (!this.contactTypes.length) {
          const response = await firstValueFrom(this.contactsDataService.getContactTypes());
          this.contactTypes = response.success ? response.data : [];
        }
        break;
      case 'country':
        if (!this.countries.length) {
          this.countries = (await AddressHelper.getCountries())
            .filter(country => country.IsoCode);
        }
        break;
      case 'status':
        if (!this.leadStatuses.length) {
          const response = await firstValueFrom(this.leadDataService.getLeadStatus());
          this.leadStatuses = response.success ? response.data : [];
        }
        break;
    }
  }

  private lockBodyScroll(): void {
    this.previousBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    this.document.body.style.overflow = this.previousBodyOverflow;
  }

  private focusMobileSearch(): void {
    const focus = () => {
      this.mobileFilterSearch?.nativeElement?.focus({preventScroll: true});
      this.mobileFilterSearch?.nativeElement?.select();
    };

    setTimeout(focus);
    requestAnimationFrame(focus);
    setTimeout(focus, 100);
  }

  private getSelectedCategoryKey(categoryKey: string | undefined): string {
    return categoryKey && categoryKey !== 'Filters' ? categoryKey : '';
  }

  private hasDateRangeChanged(): boolean {
    return this.filters?.fromDate?.valueOf() !== this.mobileDateRange?.[0]?.valueOf()
      || this.filters?.toDate?.valueOf() !== this.mobileDateRange?.[1]?.valueOf();
  }

  private formatMobileDate(date: Date, withYear = false): string {
    const normalizedDate = new Date(date);
    const day = `${normalizedDate.getDate()}`.padStart(2, '0');
    const month = `${normalizedDate.getMonth() + 1}`.padStart(2, '0');

    if (!withYear) {
      return `${day}.${month}.`;
    }

    return `${day}.${month}.${normalizedDate.getFullYear()}`;
  }
}
